import { Alert, Paper, Tooltip, Typography } from "@mui/material";
import { DataGridPro, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { useMemo, useState } from "react";
import { usePermissions } from "../../../../../server/auth/use-permissions";
import { ClipboardList } from "../../../../../styles/icons/clipboard-list";
import { Scrollbar } from "../../../../generic/components/scrollbar";
import { SeverityPill } from "../../../../generic/components/severity-pill";
import { AddCustomEmailDomain } from "./add-domain";
import { CNameRecordsDialog } from "./cnames-dialog";
import { DeleteButton } from "./delete-button";
import { NoCustomDomainsMessage } from "./no-domains";
import { RefreshButton } from "./refresh-button";

import type { CustomEmailDomain } from "../../server/supabase-custom-email-domain-service";
import type { GridColDef, GridRowParams } from "@mui/x-data-grid-pro";
import type { FC } from "react";

export const EmailDomainConfigurationTable: FC<{
  customDomains: CustomEmailDomain[];
  reload: () => void;
}> = ({ customDomains, reload }) => {
  const { ce_admin } = usePermissions();

  const [cnameDialogCustomEmailDomain, setCnameDialogCustomEmailDomain] =
    useState<CustomEmailDomain | null>(null);

  const columns: GridColDef<CustomEmailDomain>[] = useMemo(
    () => [
      {
        field: "domain",
        headerName: "Domain",
        flex: 3,
      },
      {
        field: "verified",
        headerName: "Status",
        flex: 0.5,
        renderCell: (params) => (
          <SeverityPill color={params.row.verified ? "success" : "info"}>
            {params.row.verified ? "Active" : "Pending"}
          </SeverityPill>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1,
        type: "actions",
        // oxlint-disable-next-line react/no-unstable-nested-components -- Row-actions callback returns elements; DataGrid calls it as a function
        getActions: (params: GridRowParams<CustomEmailDomain>) => {
          const actions = [
            <GridActionsCellItem
              key="cname"
              icon={
                <Tooltip title={`View CNAME Records for ${params.row.domain}`} placement="right">
                  <ClipboardList fontSize="small" />
                </Tooltip>
              }
              label={`View CNAME Records for ${params.row.domain}`}
              aria-label={`View CNAME Records for ${params.row.domain}`}
              onClick={() => setCnameDialogCustomEmailDomain(params.row)}
              showInMenu={false}
            />,
          ];

          if (!params.row.verified) {
            actions.unshift(<RefreshButton customEmailDomain={params.row} onVerified={reload} />);
            actions.push(<DeleteButton customEmailDomain={params.row} onDelete={reload} />);
          }

          return actions;
        },
      },
    ],
    [reload],
  );

  return (
    <Scrollbar>
      {customDomains.length === 0 && <NoCustomDomainsMessage />}
      {ce_admin ? (
        <AddCustomEmailDomain onAdd={reload} />
      ) : (
        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2">
            {customDomains.length === 0
              ? "Please contact support to configure a custom domain"
              : "Please contact support to configure an additional custom domain"}
          </Typography>
        </Alert>
      )}
      {customDomains.length > 0 && (
        <Paper sx={{ mt: 2, width: "100%" }}>
          <DataGridPro
            rows={customDomains}
            getRowId={(row) => row.id}
            columns={columns}
            disableRowSelectionOnClick
            initialState={{
              sorting: {
                sortModel: [{ field: "domain", sort: "asc" }],
              },
            }}
          />
        </Paper>
      )}
      {cnameDialogCustomEmailDomain && (
        <CNameRecordsDialog
          customEmailDomain={cnameDialogCustomEmailDomain}
          onClose={() => setCnameDialogCustomEmailDomain(null)}
        />
      )}
    </Scrollbar>
  );
};
